// src/components/CalendarView/CalendarWeekView.tsx
import React, { useMemo } from 'react';
import type { CalendarEvent, ActionMenuConfig } from './types';
import { CalendarEventCard } from './CalendarEventCard';
import { getCalendarEventKey } from './calendarUtils';

const DAY_ABBREV = ['SUN', 'MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT'];
const MONTH_ABBREV = [
  'JAN',
  'FEB',
  'MAR',
  'APR',
  'MAY',
  'JUN',
  'JUL',
  'AUG',
  'SEP',
  'OCT',
  'NOV',
  'DEC',
];

function toDateKey(d: Date): string
{
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function getEventDateKey(event: CalendarEvent): string
{
  const d = new Date(event.Date);
  if (isNaN(d.getTime())) return '';
  return toDateKey(d);
}

interface CalendarWeekViewProps {
  events: CalendarEvent[];
  currentDate: Date;
  isLoading: boolean;
  onOpenInfoWindow: (conversationId: number) => void;
  actionMenuConfig: ActionMenuConfig;
  onDuplicateItem?: (e: any, conv: any) => void;
}

export const CalendarWeekView: React.FC<CalendarWeekViewProps> = ({
  events,
  currentDate,
  isLoading,
  onOpenInfoWindow,
  actionMenuConfig,
  onDuplicateItem,
}) =>
{
  const weekDays = useMemo(() =>
  {
    const start = new Date(currentDate);
    start.setDate(start.getDate() - start.getDay());
    return Array.from({ length: 7 }, (_, i) =>
    {
      const d = new Date(start);
      d.setDate(d.getDate() + i);
      return d;
    });
  }, [currentDate]);

  const eventMap = useMemo(() =>
  {
    const map = new Map<string, CalendarEvent[]>();
    events.forEach(ev =>
    {
      const key = getEventDateKey(ev);
      if (!key) return;
      if (!map.has(key)) map.set(key, []);
      map.get(key)!.push(ev);
    });
    return map;
  }, [events]);

  const today = new Date();
  const todayKey = toDateKey(today);

  return (
    <div className="cal-week-grid">
      {weekDays.map(day =>
      {
        const key = toDateKey(day);
        const isToday = key === todayKey;
        const dow = day.getDay();
        const isWeekend = dow === 0 || dow === 6;
        return (
          <div
            key={key}
            className={`cal-week-day-header${isToday ? ' cal-today-num' : ''}${isWeekend ? ' is-weekend' : ''}`}
          >
            <div className="cal-week-day-name">{DAY_ABBREV[day.getDay()]}</div>
            <div className="cal-week-day-num">
              {day.getDate()} {MONTH_ABBREV[day.getMonth()]}
            </div>
          </div>
        );
      })}
      {weekDays.map(day =>
      {
        const key = toDateKey(day);
        const dayEvents = isLoading ? [] : eventMap.get(key) || [];
        const dow = day.getDay();
        const isWeekend = dow === 0 || dow === 6;
        return (
          <div key={`col-${key}`} className={`cal-week-day-col${isWeekend ? ' is-weekend' : ''}`}>
            {isLoading ? (
              <div className="cal-skeleton cal-skeleton-card" />
            ) : (
              dayEvents.map(ev => (
                <CalendarEventCard
                  key={getCalendarEventKey(ev)}
                  event={ev}
                  onOpenInfoWindow={onOpenInfoWindow}
                  actionMenuConfig={actionMenuConfig}
                  onDuplicateItem={onDuplicateItem}
                />
              ))
            )}
          </div>
        );
      })}
    </div>
  );
};

export default CalendarWeekView;
